import { RecaptchaGuardMessage, RECAPTCHA_RETRY_ALLOWED } from "@simplyk/common";

import { RecaptchaAction } from "../constants/recaptcha";

export interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  execute(siteKey: string, options?: { action?: string }): PromiseLike<string>;
}

export const RECAPTCHA_READY_TIMEOUT_MS = 10_000;
export const RECAPTCHA_EXECUTE_TIMEOUT_MS = 30_000;

export class RecaptchaTimeoutError extends Error {
  constructor(step: "ready" | "execute", timeoutMs: number) {
    super(`Recaptcha ${step} did not answer within ${timeoutMs} ms`);
  }
}

const rejectAfter = <T>(promise: PromiseLike<T>, step: "ready" | "execute", timeoutMs: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new RecaptchaTimeoutError(step, timeoutMs)), timeoutMs);
    Promise.resolve(promise)
      .then(resolve, reject)
      .finally(() => clearTimeout(timeoutId));
  });

export const requestRecaptchaToken = async (
  grecaptcha: GrecaptchaEnterprise,
  action: RecaptchaAction
): Promise<string> => {
  await rejectAfter(new Promise<void>((resolve) => grecaptcha.ready(resolve)), "ready", RECAPTCHA_READY_TIMEOUT_MS);
  const token = await rejectAfter(
    grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY!, { action }),
    "execute",
    RECAPTCHA_EXECUTE_TIMEOUT_MS
  );
  if (!token) {
    throw new Error("Not able to fetch recaptcha token");
  }
  return token;
};

interface RecaptchaRejection {
  errorMessage: string | undefined;
  hasTimedOut: boolean;
  retryCount: number;
}

export const shouldRetryRecaptchaRejection = ({ errorMessage, hasTimedOut, retryCount }: RecaptchaRejection): boolean =>
  errorMessage === RecaptchaGuardMessage && !hasTimedOut && retryCount < RECAPTCHA_RETRY_ALLOWED;
